/**
 * Fixed Route Timetable Visualization Component
 * 
 * Displays a timeline/Gantt chart showing vehicle route assignments for fixed route fleets.
 * Shows which vehicles serve which routes at what times, with loop numbers for circulators.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Tooltip, Chip, Divider } from '@mui/material';

import { Vehicle } from '../../../../types/vehicle';
import { OperatingPeriod, BreakPeriod } from '../../../../types/simulation';
import { RouteAssignment } from '../../../../types/fixedRoutes';
import {
  parseTime,
  formatTime,
  calculateTimeDifference,
  getTimeLabels,
} from './timeUtils';

import { Icon } from '@liftango/ui-icons';
interface FixedRouteTimetableVisualizationProps {
  vehicles: Vehicle[];
  /** Start time in minutes from midnight (e.g., 360 for 06:00) */
  serviceStartMinutes: number;
  /** End time in minutes from midnight (e.g., 1320 for 22:00) */
  serviceEndMinutes: number;
  /** Time interval for blocks (e.g., 30 for 30-minute blocks) */
  intervalMinutes?: number;
  interactive?: boolean;
  onBlockClick?: (
    vehicleId: string,
    blockStartMinutes: number,
    status: 'working' | 'break' | 'off'
  ) => void;
}

/**
 * Generate a consistent color for a route based on its ID
 */
function getRouteColor(routeId: string, index: number): string {
  // Use hash-like approach for consistent colors per route
  const colors = [
    '#4caf50', // Green
    '#2196f3', // Blue
    '#9c27b0', // Purple
    '#00bcd4', // Cyan
    '#f44336', // Red
    '#ff9800', // Orange
    '#795548', // Brown
    '#607d8b', // Blue Grey
    '#e91e63', // Pink
    '#3f51b5', // Indigo
  ];
  
  // Use route ID hash to pick color consistently
  let hash = 0;
  for (let i = 0; i < routeId.length; i++) {
    hash = ((hash << 5) - hash) + routeId.charCodeAt(i);
    hash = hash & hash;
  }
  return colors[Math.abs(hash) % colors.length];
}

/**
 * Check if a time block overlaps with a route assignment period
 * Two ranges overlap if: blockStart < periodEnd AND blockEnd > periodStart
 */
function blockOverlapsAssignment(
  blockStartMinutes: number,
  blockEndMinutes: number,
  periodStartMinutes: number,
  periodEndMinutes: number
): boolean {
  // Normalize times (handle same-day only for now, overnight is edge case)
  // For same-day periods (most common): periodEnd >= periodStart
  // For overnight periods: periodEnd < periodStart
  
  let normalizedPeriodEnd = periodEndMinutes;
  let normalizedBlockEnd = blockEndMinutes;
  
  // Handle overnight periods
  if (periodEndMinutes < periodStartMinutes) {
    normalizedPeriodEnd = periodEndMinutes + (24 * 60);
  }
  
  if (blockEndMinutes < blockStartMinutes) {
    normalizedBlockEnd = blockEndMinutes + (24 * 60);
  }
  
  // Two ranges overlap if: start1 < end2 AND end1 > start2
  // In our case: blockStart < periodEnd AND blockEnd > periodStart
  return blockStartMinutes < normalizedPeriodEnd && normalizedBlockEnd > periodStartMinutes;
}

const FixedRouteTimetableVisualization: React.FC<FixedRouteTimetableVisualizationProps> = ({
  vehicles,
  serviceStartMinutes,
  serviceEndMinutes,
  intervalMinutes = 30,
  interactive = false,
  onBlockClick,
}) => {
  const { t } = useTranslation('mapWizards');
  // Debug: Log breaks found in vehicles
  React.useEffect(() => {
    vehicles.forEach((vehicle) => {
      vehicle.operatingHours.forEach((period, periodIdx) => {
        if (period.breaks && period.breaks.length > 0) {
          console.log(
            `[Visualization] Vehicle ${vehicle.name}, Period ${periodIdx + 1} ` +
            `(${period.start}-${period.end}): ${period.breaks.length} break(s):`,
            period.breaks.map((b: BreakPeriod) => `${b.start}-${b.end}`).join(', ')
          );
        }
      });
    });
  }, [vehicles]);
  // Collect all unique routes for color assignment
  const routeMap = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    vehicles.forEach((vehicle, vehicleIdx) => {
      vehicle.operatingHours.forEach((period) => {
        if (period.routeAssignments) {
          period.routeAssignments.forEach((assignment: RouteAssignment, idx: number) => {
            if (!map.has(assignment.routeId)) {
              map.set(assignment.routeId, {
                name: assignment.routeName,
                color: getRouteColor(assignment.routeId, map.size),
              });
            }
          });
        }
      });
    });
    return map;
  }, [vehicles]);

  // Generate time labels for the header
  const timeLabels = getTimeLabels(serviceStartMinutes, serviceEndMinutes, intervalMinutes);
  const totalMinutes = calculateTimeDifference(serviceStartMinutes, serviceEndMinutes);
  const numBlocks = Math.ceil(totalMinutes / intervalMinutes);

  // Get route assignment or break information for a specific time block
  const getBlockAssignment = (
    vehicle: Vehicle,
    blockStartMinutes: number
  ): { 
    assignment: RouteAssignment; 
    period: OperatingPeriod;
    isBreak?: boolean;
    breakInfo?: { start: string; end: string; duration: number };
  } | null => {
    const blockEndMinutes = blockStartMinutes + intervalMinutes;

    // Check all operating periods for this vehicle
    for (const period of vehicle.operatingHours) {
      const periodStart = parseTime(period.start);
      let periodEnd = parseTime(period.end);
      
      // Handle overnight periods
      if (periodEnd < periodStart) {
        periodEnd = periodEnd + (24 * 60);
      }

      // Check if block overlaps with operating period
      if (blockOverlapsAssignment(blockStartMinutes, blockEndMinutes, periodStart, periodEnd)) {
        // First, check if this block overlaps with a break period (breaks take priority over route assignments)
        // Breaks are part of the operating period, so check them when the block is within the period
        if (period.breaks && period.breaks.length > 0) {
          for (const breakPeriod of period.breaks) {
            const breakStart = parseTime(breakPeriod.start);
            let breakEnd = parseTime(breakPeriod.end);
            
            // Handle overnight breaks
            if (breakEnd < breakStart) {
              breakEnd = breakEnd + (24 * 60);
            }

            // Check if block overlaps with break period
            // Use the same overlap function for consistency
            if (blockOverlapsAssignment(blockStartMinutes, blockEndMinutes, breakStart, breakEnd)) {
              const breakDuration = calculateTimeDifference(breakStart, breakEnd);
              return {
                assignment: period.routeAssignments?.[0] || {} as RouteAssignment, // Provide fallback for type
                period,
                isBreak: true,
                breakInfo: {
                  start: breakPeriod.start,
                  end: breakPeriod.end,
                  duration: breakDuration,
                },
              };
            }
          }
        }

        // Not a break, check if this period has route assignments
        if (period.routeAssignments && period.routeAssignments.length > 0) {
          // Return the first assignment (in Phase 1B, shifts have one consolidated assignment)
          return {
            assignment: period.routeAssignments[0],
            period,
            isBreak: false,
          };
        }
      }
    }

    return null;
  };

  // Calculate vehicles serving each route per time block
  const getRouteActivity = (blockStartMinutes: number): Map<string, number> => {
    const activity = new Map<string, number>();
    
    vehicles.forEach((vehicle) => {
      const blockAssignment = getBlockAssignment(vehicle, blockStartMinutes);
      if (blockAssignment) {
        const routeId = blockAssignment.assignment.routeId;
        activity.set(routeId, (activity.get(routeId) || 0) + 1);
      }
    });
    
    return activity;
  };

  const getTimelineStatus = (
    blockAssignment: ReturnType<typeof getBlockAssignment>
  ): 'working' | 'break' | 'off' => {
    if (!blockAssignment) return 'off';
    if (blockAssignment.isBreak) return 'break';
    return 'working';
  };

  const handleBlockInteraction = (
    vehicleId: string,
    blockStartMinutes: number,
    status: 'working' | 'break' | 'off'
  ) => {
    if (!interactive || !onBlockClick) return;
    onBlockClick(vehicleId, blockStartMinutes, status);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('autoFleetGen.timeline.routeAssignmentTitle')}
      </Typography>

      {/* Legend */}
      <Box display="flex" gap={3} mb={2} flexWrap="wrap" alignItems="center">
        <Box display="flex" gap={2} alignItems="center">
          <Typography variant="caption" sx={{ fontWeight: 600, mr: 1 }}>
            {t('autoFleetGen.timeline.routes')}
          </Typography>
          {Array.from(routeMap.entries()).map(([routeId, { name, color }]) => (
            <Box key={routeId} display="flex" alignItems="center" gap={0.5}>
              <Box width={20} height={20} bgcolor={color} borderRadius={1} />
              <Typography variant="caption">{name}</Typography>
            </Box>
          ))}
          {routeMap.size === 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('autoFleetGen.timeline.noRouteAssignments')}
            </Typography>
          )}
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box display="flex" gap={1} alignItems="center">
          <Box
            sx={{
              width: 20,
              height: 20,
              bgcolor: '#757575',
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.1) 5px, rgba(255,255,255,0.1) 10px)',
              borderRadius: 1,
              border: '2px solid #616161',
            }}
          />
          <Typography variant="caption">{t('autoFleetGen.timeline.break')}</Typography>
        </Box>
      </Box>

      {/* Timeline Grid */}
      <Paper sx={{ p: 2, overflowX: 'auto' }}>
        <Box sx={{ minWidth: Math.max(1200, numBlocks * 40 + 150) }}>
          {/* Header with time labels */}
          <Box display="grid" gridTemplateColumns={`150px repeat(${numBlocks}, minmax(40px, 1fr))`} gap={0.5} mb={1}>
            <Box /> {/* Empty cell for vehicle names column */}
            {timeLabels.slice(0, -1).map((label, idx) => (
              <Box
                key={idx}
                sx={{
                  py: 0.5,
                  borderRadius: 1,
                  position: 'relative',
                  minHeight: '24px',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: -2,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    whiteSpace: 'nowrap',
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Vehicle rows */}
          {vehicles.map((vehicle, vehicleIdx) => {
            // Extract base vehicle name (remove " - Shift N" suffix if present)
            const baseVehicleName = vehicle.name.replace(/\s*-\s*Shift\s+\d+$/, '');
            const prevBaseName = vehicleIdx > 0 
              ? vehicles[vehicleIdx - 1]?.name.replace(/\s*-\s*Shift\s+\d+$/, '') 
              : null;
            
            // Determine if this is a new vehicle group (different from previous)
            const isNewVehicle = vehicleIdx === 0 || baseVehicleName !== prevBaseName;
            
            // Calculate vehicle index based on unique base names
            const vehicleGroupIndex = vehicles.slice(0, vehicleIdx + 1)
              .map(v => v.name.replace(/\s*-\s*Shift\s+\d+$/, ''))
              .filter((name, idx, arr) => arr.indexOf(name) === idx)
              .indexOf(baseVehicleName);
            
            const vehicleColor = `hsl(${(vehicleGroupIndex * 137.5) % 360}, 70%, 92%)`;
            const vehicleBorderColor = `hsl(${(vehicleGroupIndex * 137.5) % 360}, 50%, 70%)`;
            const hasMultipleShifts = vehicle.operatingHours.length > 1;
            
            // Extract shift number from vehicle name if present
            const shiftMatch = vehicle.name.match(/Shift\s+(\d+)/);
            const shiftNumber = shiftMatch ? parseInt(shiftMatch[1], 10) : null;
            
            // Count total shifts for this vehicle (group all shifts with same base name)
            const totalShiftsForVehicle = vehicles.filter(
              v => v.name.replace(/\s*-\s*Shift\s+\d+$/, '') === baseVehicleName
            ).length;

            return (
              <React.Fragment key={vehicle.id}>
                {/* Vehicle header row - show once per unique vehicle */}
                {isNewVehicle && (
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      bgcolor: vehicleColor,
                      border: `2px solid ${vehicleBorderColor}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                      mb: 1,
                      mt: vehicleIdx > 0 ? 2 : 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      boxShadow: 2,
                    }}
                  >
                    <Icon size={28} name="truck" style={{ color: vehicleBorderColor}} />
                    <Box flex={1}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {baseVehicleName}
                      </Typography>
                      {totalShiftsForVehicle > 1 && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5, 
                            mt: 0.5,
                            fontWeight: 500,
                          }}
                        >
                          <Icon size={14} name="clock" />
                          {t(
                            totalShiftsForVehicle === 1
                              ? 'autoFleetGen.timeline.shiftsAllocatedOne'
                              : 'autoFleetGen.timeline.shiftsAllocatedOther',
                            { total: totalShiftsForVehicle }
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Shift row */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `150px repeat(${numBlocks}, minmax(40px, 1fr))`,
                    gap: 0.5,
                    mb: 0.5,
                    ml: totalShiftsForVehicle > 1 ? 3 : 0,
                    position: 'relative',
                  }}
                >
                  {/* Shift label (left column) */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      px: 1,
                      py: 0.75,
                      bgcolor: totalShiftsForVehicle > 1 ? '#f8f9fa' : 'transparent',
                      borderRadius: 1,
                      border: totalShiftsForVehicle > 1 ? `1px solid ${vehicleBorderColor}` : 'none',
                      borderLeft: totalShiftsForVehicle > 1 ? `3px solid ${vehicleBorderColor}` : 'none',
                      minHeight: 40,
                    }}
                  >
                    {totalShiftsForVehicle > 1 && shiftNumber ? (
                      <>
                        <Chip
                          label={t('autoFleetGen.timeline.shiftLabel', { value: shiftNumber })}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: '#e3f2fd',
                            color: '#1976d2',
                            border: `1px solid ${vehicleBorderColor}`,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.5 }}>
                          {vehicle.operatingHours[0]?.start || ''} - {vehicle.operatingHours[0]?.end || ''}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {vehicle.operatingHours[0]?.start || ''} - {vehicle.operatingHours[0]?.end || ''}
                      </Typography>
                    )}
                  </Box>

                {/* Time blocks */}
                {Array.from({ length: numBlocks }).map((_, blockIdx) => {
                  const blockStartMinutes = serviceStartMinutes + blockIdx * intervalMinutes;
                  const blockAssignment = getBlockAssignment(vehicle, blockStartMinutes);

                  // Check if this block is at a shift boundary
                  const isShiftBoundary = blockIdx > 0 && (() => {
                    // Check if we're crossing from one shift to another within the same vehicle
                    const prevBlockStart = serviceStartMinutes + (blockIdx - 1) * intervalMinutes;
                    const prevAssignment = getBlockAssignment(vehicle, prevBlockStart);
                    if (!prevAssignment || !blockAssignment) return false;
                    
                    // If previous period is different from current period, we're at a boundary
                    return prevAssignment.period.start !== blockAssignment.period.start ||
                           prevAssignment.period.end !== blockAssignment.period.end;
                  })();

                  if (blockAssignment) {
                    // Render break block
                    if (blockAssignment.isBreak && blockAssignment.breakInfo) {
                      const breakInfo = blockAssignment.breakInfo;
                      return (
                        <Tooltip
                          key={blockIdx}
                          title={
                            <Box>
                              <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                                {t('autoFleetGen.timeline.breakLabel')}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {formatTime(blockStartMinutes)} - {formatTime(blockStartMinutes + intervalMinutes)}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t('autoFleetGen.timeline.breakPeriodLabel', { start: breakInfo.start, end: breakInfo.end })}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t('autoFleetGen.timeline.breakDurationLabel', { value: breakInfo.duration })}
                              </Typography>
                              {blockAssignment.period.routeAssignments && blockAssignment.period.routeAssignments.length > 0 && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {t('autoFleetGen.timeline.shiftRange', { start: blockAssignment.period.start, end: blockAssignment.period.end })}
                                </Typography>
                              )}
                            </Box>
                          }
                          arrow
                        >
                        <Box
                          sx={{
                            height: 40,
                            bgcolor: '#757575',
                            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.1) 5px, rgba(255,255,255,0.1) 10px)',
                            borderRadius: 1,
                            border: `2px solid #616161`,
                            borderLeft: isShiftBoundary 
                              ? `4px solid ${vehicleBorderColor}` 
                              : '2px solid #616161',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            color: '#fff',
                            cursor: interactive ? 'pointer' : 'help',
                            transition: 'all 0.2s ease',
                            px: 0.5,
                            position: 'relative',
                            boxShadow: isShiftBoundary ? 2 : 1,
                            '&:hover': {
                              transform: 'scale(1.02)',
                              boxShadow: 4,
                              zIndex: 3,
                              bgcolor: '#616161',
                            },
                          }}
                        role={interactive ? 'button' : undefined}
                        tabIndex={interactive ? 0 : -1}
                        onClick={() =>
                          handleBlockInteraction(vehicle.id, blockStartMinutes, getTimelineStatus(blockAssignment))
                        }
                        onKeyDown={(event) => {
                          if (!interactive) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleBlockInteraction(vehicle.id, blockStartMinutes, getTimelineStatus(blockAssignment));
                          }
                        }}
                        >
                            <Icon size={16} name="coffee" style={{ opacity: 0.9 }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.5rem',
                                mt: 0.25,
                                opacity: 0.9,
                              }}
                            >
                              {t('autoFleetGen.timeline.breakLabel')}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    }

                    // Render route assignment block
                    const { assignment, period } = blockAssignment;
                    const routeInfo = routeMap.get(assignment.routeId);
                    const routeColor = routeInfo?.color || '#9e9e9e';
                    const routeName = routeInfo?.name || assignment.routeName || t('autoFleetGen.timeline.unknownRoute');

                    // Format loop numbers for display
                    const loopText = assignment.loopNumbers && assignment.loopNumbers.length > 0
                      ? assignment.loopNumbers.length === 1
                        ? t('autoFleetGen.timeline.loopOne', { value: assignment.loopNumbers[0] })
                        : t('autoFleetGen.timeline.loopRange', { min: Math.min(...assignment.loopNumbers), max: Math.max(...assignment.loopNumbers) })
                      : null;

                    // Determine shift number for tooltip (if multiple shifts)
                    const shiftNumber = hasMultipleShifts
                      ? vehicle.operatingHours.findIndex(p => p.start === period.start && p.end === period.end) + 1
                      : null;

                    return (
                      <Tooltip
                        key={blockIdx}
                        title={
                          <Box>
                            <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                              {routeName}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {formatTime(blockStartMinutes)} - {formatTime(blockStartMinutes + intervalMinutes)}
                            </Typography>
                            {period.start && period.end && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t('autoFleetGen.timeline.periodRange', { start: period.start, end: period.end })}
                              </Typography>
                            )}
                            {shiftNumber && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t('autoFleetGen.timeline.shiftOf', { shift: shiftNumber, total: vehicle.operatingHours.length })}
                              </Typography>
                            )}
                            {loopText && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {loopText}
                              </Typography>
                            )}
                            {assignment.startStop && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t('autoFleetGen.timeline.startStop', { value: assignment.startStop })}
                              </Typography>
                            )}
                            {assignment.endStop && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t('autoFleetGen.timeline.endStop', { value: assignment.endStop })}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            height: 40,
                            bgcolor: routeColor,
                            borderRadius: 1,
                            border: '1px solid rgba(0,0,0,0.15)',
                            borderLeft: isShiftBoundary 
                              ? `4px solid ${vehicleBorderColor}` 
                              : '1px solid rgba(0,0,0,0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            color: '#fff',
                            cursor: interactive ? 'pointer' : 'help',
                            transition: 'all 0.2s ease',
                            px: 0.5,
                            position: 'relative',
                            boxShadow: isShiftBoundary ? 2 : 0.5,
                            '&:hover': {
                              transform: 'scale(1.02)',
                              boxShadow: 4,
                              zIndex: 2,
                            },
                          }}
                        role={interactive ? 'button' : undefined}
                        tabIndex={interactive ? 0 : -1}
                        onClick={() =>
                          handleBlockInteraction(vehicle.id, blockStartMinutes, getTimelineStatus(blockAssignment))
                        }
                        onKeyDown={(event) => {
                          if (!interactive) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleBlockInteraction(vehicle.id, blockStartMinutes, getTimelineStatus(blockAssignment));
                          }
                        }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              textAlign: 'center',
                              fontSize: '0.65rem',
                            }}
                            title={routeName}
                          >
                            {routeName.length > 8 ? `${routeName.substring(0, 6)}...` : routeName}
                          </Typography>
                          {loopText && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.55rem',
                                opacity: 0.9,
                                mt: 0.25,
                              }}
                            >
                              {loopText.length > 10 ? `${loopText.substring(0, 8)}...` : loopText}
                            </Typography>
                          )}
                          <Icon size={13} name="bus" style={{ marginTop: 2, opacity: 0.8 }} />
                        </Box>
                      </Tooltip>
                    );
                  }

                  // No assignment for this block
                  return (
                    <Tooltip
                      key={blockIdx}
                      title={
                        <Box>
                          <Typography variant="caption" display="block">
                            {formatTime(blockStartMinutes)} - {formatTime(blockStartMinutes + intervalMinutes)}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {t('autoFleetGen.timeline.noAssignment')}
                          </Typography>
                        </Box>
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          height: 40,
                          bgcolor: '#fafafa',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                          borderLeft: isShiftBoundary 
                            ? `4px solid ${vehicleBorderColor}` 
                            : '1px solid #e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: interactive ? 'pointer' : 'help',
                        }}
                        role={interactive ? 'button' : undefined}
                        tabIndex={interactive ? 0 : -1}
                        onClick={() => handleBlockInteraction(vehicle.id, blockStartMinutes, 'off')}
                        onKeyDown={(event) => {
                          if (!interactive) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleBlockInteraction(vehicle.id, blockStartMinutes, 'off');
                          }
                        }}
                      />
                    </Tooltip>
                  );
                })}
                </Box>
              </React.Fragment>
            );
          })}

          {/* Summary row - routes active per block */}
          {routeMap.size > 0 && (
            <Box display="grid" gridTemplateColumns={`150px repeat(${numBlocks}, minmax(40px, 1fr))`} gap={0.5} mt={2} pt={2} borderTop="2px solid #e0e0e0">
              <Box display="flex" flexDirection="column" justifyContent="center" px={1}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('autoFleetGen.timeline.routesActive')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {t('autoFleetGen.timeline.vehiclesPerRoute')}
                </Typography>
              </Box>
              {Array.from({ length: numBlocks }).map((_, blockIdx) => {
                const blockStartMinutes = serviceStartMinutes + blockIdx * intervalMinutes;
                const routeActivity = getRouteActivity(blockStartMinutes);
                
                return (
                  <Box
                    key={blockIdx}
                    display="flex"
                    flexDirection="column"
                    gap={0.5}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      minHeight: 50,
                      bgcolor: '#fafafa',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                      p: 0.5,
                    }}
                  >
                    {routeActivity.size === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                        -
                      </Typography>
                    ) : (
                      Array.from(routeActivity.entries()).map(([routeId, count]) => {
                        const routeInfo = routeMap.get(routeId);
                        if (!routeInfo) return null;
                        
                        return (
                          <Box key={routeId} display="flex" alignItems="center" gap={0.5}>
                            <Box
                              width={8}
                              height={8}
                              bgcolor={routeInfo.color}
                              borderRadius="50%"
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                              {count}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default FixedRouteTimetableVisualization;

